import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {defineMessages, injectIntl, intlShape} from 'react-intl';
import {setProjectTitle} from '../reducers/project-title';

import log from '../lib/log';
import sharedMessages from '../lib/shared-messages';

import {
    LoadingStates,
    getIsLoadingUpload,
    getIsShowingWithoutId,
    onLoadedProject,
    requestProjectUpload
} from '../reducers/project-state';

import {
    openLoadingProject,
    closeLoadingProject
} from '../reducers/modals';
import {
    closeSampleMenu
} from '../reducers/menus';

/**
 * aibo: SBFileLoader はリポジトリ内においたsb3ファイルを読み込むためのComponentです
 * sb-fle-uploader.jsxに更新がある場合はここにも入れること
 *
 * <SBFileLoader>{(className, renderFileInput, handleLoadProject) => (
 *     <MyCoolComponent
 *         className={className}
 *         onClick={handleLoadProject}
 *     >
 *         {}
 *     </MyCoolComponent>
 * )}</SBFileLoader>
 */

const messages = defineMessages({
    loadError: {
        id: 'gui.projectLoader.loadError',
        defaultMessage: 'The project file that was selected failed to load.',
        description: 'An error that displays when a local project file fails to load.'
    }
});

class SBFileLoader extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'setFileInput',
            'fileLoaded',
            'onload',
            'handleClick',
            'resetFileInput'
        ]);
    }

    componentWillMount () {
        this.reader = new FileReader();
        this.reader.onload = this.onload;

        this.httpRequest = new XMLHttpRequest();

        this.httpRequest.addEventListener('loadend', this.fileLoaded);
    }
    componentDidUpdate (prevProps) {
        if (this.props.isLoadingUpload && !prevProps.isLoadingUpload && this.fileToUpload && this.reader) {
            this.reader.readAsArrayBuffer(this.fileToUpload);
        }
    }
    componentWillUnmount () {
        this.reader = null;
        this.resetFileInput();
        this.httpRequest.removeEventListener('loadend', this.fileLoaded);
    }
    fileLoaded() {
        const _this = this;

        if(_this.httpRequest.status === 200){
            _this.fileToUpload = _this.httpRequest.response;

            const {
                intl,
                isShowingWithoutId,
                loadingState,
                projectChanged,
                userOwnsProject,
            } = _this.props;

            // If user owns the project, or user has changed the project,
            // we must confirm with the user that they really intend to replace it.
            // (If they don't own the project and haven't changed it, no need to confirm.)
            let uploadAllowed = true;
            if (userOwnsProject || (projectChanged && isShowingWithoutId)) {
                uploadAllowed = confirm( // eslint-disable-line no-alert
                    intl.formatMessage(sharedMessages.replaceProjectWarning)
                );
            }
            if (uploadAllowed) {
                _this.props.requestProjectUpload(loadingState);
            } else {
                _this.props.closeSampleMenu();
            }
        }else{
            console.error(_this.httpRequest.status+' '+_this.httpRequest.statusText);
        }
    }
    setFileInput() {
        const {
            projectFilePath,
        } = this.props;

        if (projectFilePath) { // Don't attempt to load if no file was selected
            this.httpRequest.open("GET",projectFilePath);
            this.httpRequest.responseType = 'blob';
            this.httpRequest.send();
        }
    }
    resetFileInput () {
        this.fileToUpload = null;
        if (this.fileInput) {
            this.fileInput.value = null;
        }
    }
    // called when file upload raw data is available in the reader
    onload () {
        if (this.reader && this.reader.result) {
            this.props.onLoadingStarted();
            const projectTitle = this.props.projectName; // aibo:
            this.props.vm.loadProject(this.reader.result)
                .then(() => {
                    this.props.onLoadingFinished(this.props.loadingState, true);
                    // Reset the file input after project is loaded
                    // This is necessary in case the user wants to reload a project
                    if (projectTitle) { // aibo: 
                        this.props.onReceivedProjectTitle(projectTitle);
                    }
                    this.resetFileInput();
                })
                .catch(error => {
                    log.warn(error);
                    alert(this.props.intl.formatMessage(messages.loadError)); // eslint-disable-line no-alert
                    this.props.onLoadingFinished(this.props.loadingState, false);
                    // Reset the file input after project is loaded
                    // This is necessary in case the user wants to reload a project
                    this.resetFileInput();
                });
        }
    }
    handleClick () {
        this.setFileInput();
    }
    render () {
        return this.props.children(this.props.className, this.props.projectName, this.handleClick);
    }
}

SBFileLoader.propTypes = {
    canSave: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    children: PropTypes.func,
    className: PropTypes.string,
    closeSampleMenu: PropTypes.func, // aibo: 
    intl: intlShape.isRequired,
    isLoadingUpload: PropTypes.bool,
    isShowingWithoutId: PropTypes.bool,
    loadingState: PropTypes.oneOf(LoadingStates),
    onLoadingFinished: PropTypes.func,
    onLoadingStarted: PropTypes.func,
    projectChanged: PropTypes.bool,
    requestProjectUpload: PropTypes.func,
    onReceivedProjectTitle: PropTypes.func,
    userOwnsProject: PropTypes.bool,
    vm: PropTypes.shape({
        loadProject: PropTypes.func
    }),
    projectFilePath: PropTypes.string,
    projectName: PropTypes.string,
};
SBFileLoader.defaultProps = {
    className: '',
    projectFilePath: '',
    projectName: '',
};
const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    return {
        isLoadingUpload: getIsLoadingUpload(loadingState),
        isShowingWithoutId: getIsShowingWithoutId(loadingState),
        loadingState: loadingState,
        projectChanged: state.scratchGui.projectChanged,
        vm: state.scratchGui.vm
    };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
    closeSampleMenu: () => dispatch(closeSampleMenu()),
    onLoadingFinished: (loadingState, success) => {
        dispatch(onLoadedProject(loadingState, ownProps.canSave, success));
        dispatch(closeLoadingProject());
        dispatch(closeSampleMenu());
    },
    requestProjectUpload: loadingState => dispatch(requestProjectUpload(loadingState)),
    onLoadingStarted: () => dispatch(openLoadingProject()),
    onReceivedProjectTitle: title => dispatch(setProjectTitle(title))
});

// Allow incoming props to override redux-provided props. Used to mock in tests.
const mergeProps = (stateProps, dispatchProps, ownProps) => Object.assign(
    {}, stateProps, dispatchProps, ownProps
);

export default connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(injectIntl(SBFileLoader));
